import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './ProfilePage.css';

interface UserProfile {
  id: number;
  username: string;
  email: string;
  fullName: string;
  phone: string;
  address: {
    street: string;
    detail: string;
    zipCode: string;
  };
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    collectionReminders: boolean;
    paymentReminders: boolean;
    promotions: boolean;
  };
  preferences: {
    language: string;
    timezone: string;
    collectionsPerWeek: number;
    autoPayment: boolean;
    dataSharing: boolean;
  };
  subscription: {
    type: 'basic' | 'premium' | 'enterprise';
    status: 'active' | 'inactive' | 'trial';
    renewalDate: string;
  };
}

export function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState('');

  // Mock profile data
  const mockProfile: UserProfile = {
    id: 1,
    username: user?.username || 'user123',
    email: user?.email || 'user@example.com',
    fullName: user?.full_name || '김환경',
    phone: '010-1234-5678',
    address: {
      street: '서울시 강남구 테헤란로 123',
      detail: '1층 101호',
      zipCode: '06234'
    },
    notifications: {
      email: true,
      sms: true,
      push: true,
      collectionReminders: true,
      paymentReminders: true,
      promotions: false
    },
    preferences: {
      language: 'ko',
      timezone: 'Asia/Seoul',
      collectionsPerWeek: 2,
      autoPayment: true,
      dataSharing: false
    },
    subscription: {
      type: 'basic',
      status: 'active',
      renewalDate: '2025-12-31'
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      // API 호출 시뮬레이션
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProfile(mockProfile);
      setLoading(false);
    };

    loadProfile();
  }, []);

  const handleInputChange = (field: string, value: any, section?: string) => {
    if (!profile) return;

    setProfile(prev => {
      if (!prev) return prev;
      
      if (section) {
        const sectionData = prev[section as keyof UserProfile];
        return {
          ...prev,
          [section]: {
            ...(typeof sectionData === 'object' ? sectionData : {}),
            [field]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    // API 저장 시뮬레이션
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSaveMessage('프로필이 성공적으로 저장되었습니다!');
    setIsEditing(false);
    setLoading(false);
    
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handlePasswordChange = async () => {
    // 비밀번호 변경 로직
    alert('비밀번호 변경 기능은 곧 구현될 예정입니다.');
  };

  const handleAccountDelete = async () => {
    const confirmed = window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
    if (confirmed) {
      alert('계정 삭제 기능은 곧 구현될 예정입니다.');
    }
  };

  const getSubscriptionBadge = (type: string) => {
    const badges = {
      basic: { label: '기본', class: 'basic', icon: '🌱' },
      premium: { label: '프리미엄', class: 'premium', icon: '⭐' },
      enterprise: { label: '기업', class: 'enterprise', icon: '🏢' }
    };
    return badges[type as keyof typeof badges] || badges.basic;
  };

  if (loading && !profile) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <LoadingSpinner />
          <p>프로필 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <h3>프로필을 불러올 수 없습니다</h3>
          <p>다시 시도해 주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-info">
            <div className="profile-avatar">
              <span className="avatar-text">{profile.fullName.charAt(0)}</span>
            </div>
            <div className="profile-details">
              <h1>{profile.fullName}</h1>
              <p className="profile-email">{profile.email}</p>
              <div className="subscription-badge">
                <span className={`badge ${getSubscriptionBadge(profile.subscription.type).class}`}>
                  {getSubscriptionBadge(profile.subscription.type).icon} {getSubscriptionBadge(profile.subscription.type).label}
                </span>
              </div>
            </div>
          </div>
          <div className="profile-actions">
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="edit-btn">
                ✏️ 편집
              </button>
            ) : (
              <div className="edit-actions">
                <button onClick={handleSave} className="save-btn" disabled={loading}>
                  {loading ? '저장 중...' : '💾 저장'}
                </button>
                <button onClick={() => setIsEditing(false)} className="cancel-btn">
                  ❌ 취소
                </button>
              </div>
            )}
          </div>
        </div>

        {saveMessage && (
          <div className="save-message success">
            ✅ {saveMessage}
          </div>
        )}

        <div className="profile-tabs">
          <button 
            className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            👤 개인정보
          </button>
          <button 
            className={`tab-btn ${activeTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            📍 주소 관리
          </button>
          <button 
            className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            🔔 알림 설정
          </button>
          <button 
            className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            ⚙️ 환경설정
          </button>
          <button 
            className={`tab-btn ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            💳 구독 관리
          </button>
          <button 
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            🔐 보안
          </button>
        </div>

        <div className="profile-content">
          {activeTab === 'personal' && (
            <div className="tab-content">
              <h2>개인정보</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="fullName">이름</label>
                  <input
                    type="text"
                    id="fullName"
                    value={profile.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="username">사용자명</label>
                  <input
                    type="text"
                    id="username"
                    value={profile.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">이메일</label>
                  <input
                    type="email"
                    id="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">전화번호</label>
                  <input
                    type="tel"
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'address' && (
            <div className="tab-content">
              <h2>주소 관리</h2>
              <div className="address-section">
                <div className="form-group">
                  <label htmlFor="street">도로명 주소</label>
                  <input
                    type="text"
                    id="street"
                    value={profile.address.street}
                    onChange={(e) => handleInputChange('street', e.target.value, 'address')}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="detail">상세 주소</label>
                  <input
                    type="text"
                    id="detail"
                    value={profile.address.detail}
                    onChange={(e) => handleInputChange('detail', e.target.value, 'address')}
                    disabled={!isEditing}
                    className="form-input"
                    placeholder="동/호수, 건물명 등"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="zipCode">우편번호</label>
                  <input
                    type="text"
                    id="zipCode"
                    value={profile.address.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value, 'address')}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                {isEditing && (
                  <button className="address-search-btn">
                    🔍 주소 검색
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="tab-content">
              <h2>알림 설정</h2>
              <div className="notifications-section">
                <div className="notification-group">
                  <h3>알림 수단</h3>
                  <div className="notification-options">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profile.notifications.email}
                        onChange={(e) => handleInputChange('email', e.target.checked, 'notifications')}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      📧 이메일 알림
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profile.notifications.sms}
                        onChange={(e) => handleInputChange('sms', e.target.checked, 'notifications')}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      📱 SMS 알림
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profile.notifications.push}
                        onChange={(e) => handleInputChange('push', e.target.checked, 'notifications')}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      🔔 푸시 알림
                    </label>
                  </div>
                </div>
                
                <div className="notification-group">
                  <h3>알림 종류</h3>
                  <div className="notification-options">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profile.notifications.collectionReminders}
                        onChange={(e) => handleInputChange('collectionReminders', e.target.checked, 'notifications')}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      🥬 수거 알림
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profile.notifications.paymentReminders}
                        onChange={(e) => handleInputChange('paymentReminders', e.target.checked, 'notifications')}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      💳 결제 알림
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profile.notifications.promotions}
                        onChange={(e) => handleInputChange('promotions', e.target.checked, 'notifications')}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      🎁 프로모션 알림
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="tab-content">
              <h2>환경설정</h2>
              <div className="preferences-section">
                <div className="form-group">
                  <label htmlFor="language">언어</label>
                  <select
                    id="language"
                    value={profile.preferences.language}
                    onChange={(e) => handleInputChange('language', e.target.value, 'preferences')}
                    disabled={!isEditing}
                    className="form-select"
                  >
                    <option value="ko">한국어</option>
                    <option value="en">English</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="collectionsPerWeek">주간 수거 횟수</label>
                  <select
                    id="collectionsPerWeek"
                    value={profile.preferences.collectionsPerWeek}
                    onChange={(e) => handleInputChange('collectionsPerWeek', parseInt(e.target.value), 'preferences')}
                    disabled={!isEditing}
                    className="form-select"
                  >
                    <option value={1}>주 1회</option>
                    <option value={2}>주 2회</option>
                    <option value={3}>주 3회</option>
                    <option value={7}>매일</option>
                  </select>
                </div>

                <div className="preference-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={profile.preferences.autoPayment}
                      onChange={(e) => handleInputChange('autoPayment', e.target.checked, 'preferences')}
                      disabled={!isEditing}
                    />
                    <span className="checkmark"></span>
                    💳 자동 결제 활성화
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={profile.preferences.dataSharing}
                      onChange={(e) => handleInputChange('dataSharing', e.target.checked, 'preferences')}
                      disabled={!isEditing}
                    />
                    <span className="checkmark"></span>
                    📊 데이터 분석을 위한 정보 공유 동의
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="tab-content">
              <h2>구독 관리</h2>
              <div className="subscription-section">
                <div className="current-plan">
                  <div className="plan-info">
                    <h3>현재 플랜</h3>
                    <div className="plan-details">
                      <span className={`plan-badge ${profile.subscription.type}`}>
                        {getSubscriptionBadge(profile.subscription.type).icon} {getSubscriptionBadge(profile.subscription.type).label}
                      </span>
                      <span className={`status-badge ${profile.subscription.status}`}>
                        {profile.subscription.status === 'active' ? '활성' : '비활성'}
                      </span>
                    </div>
                    <p>갱신일: {new Date(profile.subscription.renewalDate).toLocaleDateString('ko-KR')}</p>
                  </div>
                  <button className="upgrade-btn">
                    ⬆️ 플랜 업그레이드
                  </button>
                </div>

                <div className="plan-comparison">
                  <h3>플랜 비교</h3>
                  <div className="plans-grid">
                    <div className="plan-card basic">
                      <h4>🌱 기본</h4>
                      <div className="plan-price">월 9,900원</div>
                      <ul className="plan-features">
                        <li>주 2회 수거</li>
                        <li>기본 분석 리포트</li>
                        <li>이메일 지원</li>
                      </ul>
                    </div>
                    <div className="plan-card premium">
                      <h4>⭐ 프리미엄</h4>
                      <div className="plan-price">월 19,900원</div>
                      <ul className="plan-features">
                        <li>무제한 수거</li>
                        <li>상세 분석 리포트</li>
                        <li>실시간 채팅 지원</li>
                        <li>우선 수거 서비스</li>
                      </ul>
                    </div>
                    <div className="plan-card enterprise">
                      <h4>🏢 기업</h4>
                      <div className="plan-price">맞춤 견적</div>
                      <ul className="plan-features">
                        <li>대량 수거 서비스</li>
                        <li>전담 매니저</li>
                        <li>API 연동</li>
                        <li>맞춤형 솔루션</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="tab-content">
              <h2>보안 설정</h2>
              <div className="security-section">
                <div className="security-item">
                  <div className="security-info">
                    <h3>비밀번호 변경</h3>
                    <p>정기적인 비밀번호 변경으로 계정을 안전하게 보호하세요.</p>
                  </div>
                  <button onClick={handlePasswordChange} className="security-btn">
                    🔑 비밀번호 변경
                  </button>
                </div>

                <div className="security-item">
                  <div className="security-info">
                    <h3>2단계 인증</h3>
                    <p>추가 보안 계층으로 계정을 더욱 안전하게 보호하세요.</p>
                  </div>
                  <button className="security-btn">
                    🛡️ 2단계 인증 설정
                  </button>
                </div>

                <div className="security-item">
                  <div className="security-info">
                    <h3>로그인 내역</h3>
                    <p>최근 로그인 활동을 확인하세요.</p>
                  </div>
                  <button className="security-btn">
                    📋 내역 확인
                  </button>
                </div>

                <div className="security-item danger">
                  <div className="security-info">
                    <h3>계정 삭제</h3>
                    <p>계정을 영구적으로 삭제합니다. 이 작업은 되돌릴 수 없습니다.</p>
                  </div>
                  <button onClick={handleAccountDelete} className="security-btn danger">
                    🗑️ 계정 삭제
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}